import * as Sentry from "@sentry/browser"
import { createParser } from "eventsource-parser"

import type { CopilotMessageEvent } from "@/components/CopilotAnalyze/streaming/types"
import type { ChatMessageInterface } from "@/components/CopilotAnalyze/types"
import isFeatureEnabled from "shared/featureflags/helperFunctions"

/**
 * Asynchronously parses a ReadableStream containing server-sent events (SSE) from the Copilot API.
 *
 * This generator function processes a stream of events and yields parsed messages as they arrive.
 * It handles two types of events:
 * - "answer": The AI assistant's response messages
 * - "tool_call_start": Intermediate messages indicating tool usage (e.g., search operations)
 *
 * @param stream - A ReadableStream containing UTF-8 encoded server-sent events
 *
 * @yields {Object} Parsed event objects with the following structure:
 *   For answer/assistant events:
 *   {
 *     type: "answer",
 *     content: string,        // The actual message content
 *     span_id: string,        // Trace span ID for monitoring
 *     trace_id: string        // Trace ID for monitoring
 *   }
 *
 *   For tool call events:
 *   {
 *     type: "tool_call",
 *     content: string,        // The search intent or tool operation details
 *     span_id: string,        // Trace span ID for monitoring
 *     trace_id: string        // Trace ID for monitoring
 *   }
 *
 * @throws {Error} If stream reading fails or if event parsing encounters an error
 *
 * @example
 * ```typescript
 * const response = await fetch(url);
 * if (!response.body) throw new Error("No stream available");
 *
 * for await (const event of parseStream(response.body)) {
 *   if (event.type === "answer") {
 *     console.log("Received answer:", event.content);
 *   }
 * }
 * ```
 */
export async function* parseStream(stream: ReadableStream<Uint8Array>) {
    const reader = stream.getReader()
    const decoder = new TextDecoder()

    let data: ChatMessageInterface

    const parser = createParser({
        onEvent: (event) => {
            if (!event.data) return

            try {
                const parsedObject = JSON.parse(event.data) as CopilotMessageEvent

                if (parsedObject.type === "answer") {
                    data = {
                        id: parsedObject.message_id,
                        type: "assistant",
                        content: parsedObject.message,
                        span_id: parsedObject.span_id,
                        trace_id: parsedObject.trace_id,
                    }
                } else if (parsedObject.type === "support_answer") {
                    data = {
                        id: parsedObject.message_id,
                        type: "assistant",
                        subtype: "support_answer",
                        content: parsedObject.message,
                        span_id: parsedObject.span_id,
                        trace_id: parsedObject.trace_id,
                    }
                } else if (parsedObject.type === "support_redirect") {
                    data = {
                        id: parsedObject.message_id,
                        type: "assistant",
                        subtype: "support_redirect",
                        content: parsedObject.message,
                        span_id: parsedObject.span_id,
                        trace_id: parsedObject.trace_id,
                    }
                } else if (parsedObject.type === "tool_call_start") {
                    const toolContent = parsedObject.content
                    data = {
                        id: parsedObject.content.tool_call_id,
                        type: "intermediate",
                        subtype: "tool_call",
                        content: toolContent.tool_name,
                    }
                } else if (parsedObject.type === "clear_response") {
                    data = {
                        id: `clear_${Date.now()}`,
                        type: "clear_response",
                        content: parsedObject.content.message,
                    }
                } else {
                    // TODO: Handle more tool call types
                    data = null
                }
            } catch (e) {
                // Capture the exception and move on. Hopefully the next event can be parsed.
                data = null
            }
        },
    })

    try {
        while (true) {
            const { value, done } = await reader.read()
            if (done) break

            const chunkValue = decoder.decode(value)
            parser.feed(chunkValue)

            if (data) yield data
        }
    } finally {
        reader.releaseLock()
    }
}
